import React, { useState, useCallback, useEffect, ReactNode } from 'react'
import { SavedAddress, DeliveryInfoInput } from 'src/apollo/onlineOrdering'
import { BaseScreenProps } from '../../address_modal_base/types'
import AddressSearchScreen from '../../address_modal_base/screens/AddressSearchScreen'
import { EditAndConfirmAddress } from './edit_address/EditAndConfirmAddress'
import { t } from 'src/translations/src'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal'
import classnames from 'classnames'

export type AccountAddressScreen =
  | 'address-add'
  | 'address-edit'
  | 'address-confirm'

export interface AccountAddressScreenProps
  extends BaseScreenProps<AccountAddressScreen, SavedAddress> {
  onSuccessfulUpdate: () => void
  action: 'add' | 'edit'
}

interface AccountAddressModalProps {
  isOpen: boolean
  onClose: () => void
  selectedAddress: SavedAddress | null
  onSuccessfulUpdate: () => void
  initialScreen?: AccountAddressScreen
  action: 'add' | 'edit'
}

export const AccountAddressModal = ({
  isOpen,
  onClose,
  selectedAddress,
  onSuccessfulUpdate,
  initialScreen = 'address-confirm',
  action
}: AccountAddressModalProps) => {
  const [currentScreen, setCurrentScreen] =
    useState<AccountAddressScreen>(initialScreen)
  const [address, setAddress] = useState<SavedAddress | null>(
    selectedAddress ?? null
  )

  const handleScreenNavigation = useCallback(
    (screen: AccountAddressScreen, newAddress?: SavedAddress | null) => {
      setCurrentScreen(screen)
      if (newAddress !== undefined) {
        setAddress(newAddress)
      }
    },
    []
  )

  // Reset screen and address when modal opens or selectedAddress changes
  useEffect(() => {
    if (isOpen) {
      setCurrentScreen(initialScreen)
      setAddress(selectedAddress)
    }
  }, [isOpen, initialScreen, selectedAddress])

  const screenProps: AccountAddressScreenProps = {
    goToScreen: handleScreenNavigation,
    closeModal: onClose,
    address,
    allowBackNav: false,
    onSuccessfulUpdate,
    action
  }

  // Format Google Places result into SavedAddress type
  const formatAddress = useCallback(
    (
      normalizedAddress: DeliveryInfoInput,
      existingAddress?: SavedAddress | null
    ) => {
      const formatted: SavedAddress = {
        guid: existingAddress?.guid || address?.guid || '',
        name: existingAddress?.name || address?.name || '',
        deliveryInfo: {
          address1: normalizedAddress.address1 || '',
          address2: normalizedAddress.address2 || '',
          city: normalizedAddress.city || '',
          state: normalizedAddress.state || '',
          zipCode: normalizedAddress.zipCode || '',
          country: normalizedAddress.country || '',
          latitude: normalizedAddress.latitude,
          longitude: normalizedAddress.longitude,
          notes: existingAddress?.deliveryInfo?.notes || '',
          administrativeArea: normalizedAddress.administrativeArea || ''
        }
      }
      return formatted
    },
    [address]
  )

  // Render the appropriate screen based on current state
  const renderScreen = (
    screen: AccountAddressScreen,
    props: AccountAddressScreenProps
  ): ReactNode => {
    switch (screen) {
      case 'address-add':
        return (
          <AddressSearchScreen<
            AccountAddressScreen,
            SavedAddress,
            DeliveryInfoInput
          >
            goToScreen={props.goToScreen}
            closeModal={props.closeModal}
            address={props.address}
            allowBackNav={false}
            nextScreen='address-confirm'
            formatAddress={formatAddress}
            headerText={t('address-add-screen.title')}
            helperText={t('address-add-screen.description')}
          />
        )
      case 'address-edit':
        return (
          <AddressSearchScreen<
            AccountAddressScreen,
            SavedAddress,
            DeliveryInfoInput
          >
            goToScreen={props.goToScreen}
            closeModal={props.closeModal}
            address={props.address}
            allowBackNav={false}
            nextScreen='address-confirm'
            formatAddress={formatAddress}
            headerText={t('address-edit-screen.title')}
          />
        )
      case 'address-confirm':
        return <EditAndConfirmAddress {...props} />
      default:
        return <EditAndConfirmAddress {...props} />
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId={'account-address-modal'}
      preventOverlayClose={true}
    >
      <ModalOverlay />
      <ModalContent
        contentClassName={classnames(
          'modalContentV2 fullScreenMobile addressModal accountAddressModal'
        )}
      >
        {renderScreen(currentScreen, screenProps)}
      </ModalContent>
    </Modal>
  )
}
